import { FC, useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import { IonLabel, IonSpinner } from '@ionic/react'
import { SignInWithApple as Apple } from '@capacitor-community/apple-sign-in'
import { useMutation } from '@tanstack/react-query'

import { getEmailForAppleUserId } from '@/services/auth'
import { appConfig } from '@/services/config'
import { errorBoundary } from '@/services/errorBoundary'
import { setPushNotificationsExternalUserId } from '@/services/pushNotifications'
import { ssoSignIn } from '@/services/sso-signin'
import { useAmplitudeIdentifyUser } from '@/lib/hooks/useAmplitudeIdentifyUser'
import { useUserInfo } from '@/lib/hooks/useUserInfo'
import { Context } from '@/lib/store'
import { getStreamSetUserInfo } from '@/lib/tracking/platform/getstream'
import { Routes } from '@/router/routes'
import {
  clearLocalReferralData,
  getReferralParamData,
} from '@/utils/referralParams'

import { AuthFlow, SignInMethods } from '@/enums'

import {
  trackingReferralSignupSubmitted,
  trackingSigninCompleted,
  trackingSigninMethodSelected,
} from './TrackingSSO'

interface SignInWithAppleProps {
  authFlow: AuthFlow
  history: RouteComponentProps['history']
}

const SignInWithApple: FC<SignInWithAppleProps> = ({ authFlow, history }) => {
  const { t } = useTranslation('common')
  const { state } = useContext(Context)

  const { amplitudeIdentifyUser } = useAmplitudeIdentifyUser()

  const { data: user, refetch: getUserData } = useUserInfo({
    options: {
      retry: 5,
      enabled: false,
    },
  })

  const { mutate, isPending } = useMutation({
    mutationKey: ['signInWithApple'],
    mutationFn: signInWithApple,
    onMutate: () => trackingSigninMethodSelected(SignInMethods.Apple),
    onSuccess: async ({ _tag }) => {
      clearLocalReferralData()
      trackingSigninCompleted(SignInMethods.Apple)
      await getUserData()
      if (_tag === 'NewRegistration') {
        await trackingReferralSignupSubmitted(state)
      }
    },
    onError: (e) => {
      errorBoundary(e)
      console.error(`Error signing in with Apple ${e}`)
    },
  })

  const setupPushNotification = (userId: string) =>
    setPushNotificationsExternalUserId(userId)

  async function signInWithApple() {
    const response = await Apple.authorize({
      clientId: appConfig.appleAppId,
      redirectURI: undefined,
      scopes: 'email name',
      nonce: 'nonce',
      state: '1234',
    })

    const {
      response: { email: emailOrNull, user: appleUserId, identityToken },
    } = response

    const email =
      emailOrNull ??
      (await getEmailForAppleUserId({
        appleUserId: appleUserId,
        token: identityToken,
      }).catch((e) => {
        console.error(`Failed to get email for apple user id ${e}`)
        return null
      }))

    const { desktopUrl, refUsername } = await getReferralParamData(
      state.desktopUrl,
      state.refUsername
    )
    return ssoSignIn(
      'apple',
      {
        username: email?.toLowerCase(),
        authToken: identityToken,
      },
      {
        desktopUrl,
        refUsername,
        appleUserId,
        email: email?.toLowerCase(),
      }
    )
  }

  const onSuccess = useCallback(async () => {
    amplitudeIdentifyUser(user)
    await setupPushNotification(user.id)
    if (user.username) {
      history.replace(Routes.FEED)
      getStreamSetUserInfo(user.id, user.username)
    } else {
      history.replace(Routes.AUTH_CONSENT, { direction: 'none' })
    }
  }, [history, user, amplitudeIdentifyUser])

  useEffect(() => {
    if (user) {
      onSuccess()
    }
  }, [user, onSuccess])

  return (
    <IonLabel
      onClick={() => mutate()}
      className="cursor-pointer flex flex-row justify-center text-sm bg-white text-black font-medium py-2 px-4 rounded-full mt-4"
    >
      {isPending ? (
        <IonSpinner color="light" />
      ) : (
        <>
          <span className="mr-[8px]"></span>
          <span>
            {t(`registration.${authFlow}Options.${authFlow}WithApple`)}
          </span>
        </>
      )}
    </IonLabel>
  )
}

export default SignInWithApple
